import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, Plus, GitBranch, Search, Eye, Edit, Trash2,
  DollarSign, Clock, AlertTriangle, Bell, Power, PowerOff
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DecisionTree = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'variance_exceeded',
    action_type: 'notify',
    priority: 'medium',
    is_active: true,
    variance_threshold: 10,
    notify_roles: []
  });

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/automation/rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Failed to load automation rules:', error);
      toast.error('Failed to load automation rules');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        name: rule.name,
        description: rule.description || '',
        trigger_type: rule.trigger_type,
        action_type: rule.action_type,
        priority: rule.priority,
        is_active: rule.is_active,
        variance_threshold: rule.trigger_conditions?.variance_threshold || 10,
        notify_roles: rule.notify_roles || []
      });
    } else {
      setEditingRule(null);
      setFormData({
        name: '',
        description: '',
        trigger_type: 'variance_exceeded',
        action_type: 'notify',
        priority: 'medium',
        is_active: true,
        variance_threshold: 10,
        notify_roles: []
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRule(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: formData.name,
        description: formData.description,
        trigger_type: formData.trigger_type,
        trigger_conditions: {
          variance_threshold: parseInt(formData.variance_threshold),
          comparison: 'greater_than'
        },
        action_type: formData.action_type,
        action_config: {
          notification_title: formData.name,
          message_template: `Project variance exceeded ${formData.variance_threshold}%`
        },
        notify_roles: formData.notify_roles,
        priority: formData.priority,
        is_active: formData.is_active
      };

      if (editingRule) {
        await axios.put(
          `${API}/automation/rules/${editingRule.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Automation rule updated successfully');
      } else {
        await axios.post(
          `${API}/automation/rules`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Automation rule created successfully');
      }

      handleCloseModal();
      loadRules();
    } catch (error) {
      console.error('Failed to save automation rule:', error);
      toast.error('Failed to save automation rule');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/automation/rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rule deleted successfully');
      loadRules();
    } catch (error) {
      console.error('Failed to delete rule:', error);
      toast.error('Failed to delete rule');
    }
  };

  const toggleRuleStatus = async (rule) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/automation/rules/${rule.id}`,
        { is_active: !rule.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Rule ${!rule.is_active ? 'activated' : 'deactivated'}`);
      loadRules();
    } catch (error) {
      console.error('Failed to toggle rule status:', error);
      toast.error('Failed to update rule status');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'destructive',
      high: 'default',
      medium: 'secondary',
      low: 'outline'
    };
    return colors[priority] || 'secondary';
  };

  const getTriggerIcon = (triggerType) => {
    const icons = {
      variance_exceeded: <DollarSign size={16} />,
      status_change: <GitBranch size={16} />,
      document_created: <AlertTriangle size={16} />
    };
    return icons[triggerType] || <Bell size={16} />;
  };

  const filteredRules = rules.filter(rule =>
    rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (rule.description && rule.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Button
              onClick={() => navigate('/tools')}
              variant="outline"
              size="sm"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Tools
            </Button>
          </div>
          <h1 className="text-4xl font-black tracking-tight uppercase">Decision Tree</h1>
          <p className="text-muted-foreground mt-1">Automation Rules & Intelligent Project Oversight</p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus size={16} className="mr-2" />
          New Decision Rule
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
            <Input
              placeholder="Search automation rules..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Automation Rules */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Loading automation rules...
          </CardContent>
        </Card>
      ) : filteredRules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GitBranch size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-2">No automation rules found</p>
            <p className="text-sm text-muted-foreground mb-4">
              Create intelligent rules to monitor projects and trigger actions automatically
            </p>
            <Button onClick={() => handleOpenModal()}>
              <Plus size={16} className="mr-2" />
              Create Your First Rule
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRules.map((rule) => (
            <Card key={rule.id} className="hover:border-primary transition-all">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {getTriggerIcon(rule.trigger_type)}
                      <CardTitle className="text-lg">{rule.name}</CardTitle>
                    </div>
                    <CardDescription>{rule.description || 'No description'}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getPriorityColor(rule.priority)}>
                      {rule.priority}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleRuleStatus(rule)}
                    >
                      {rule.is_active ? (
                        <Power size={16} className="text-green-500" />
                      ) : (
                        <PowerOff size={16} className="text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Trigger</p>
                      <p className="font-medium">{rule.trigger_type.replace(/_/g, ' ')}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Action</p>
                      <p className="font-medium">{rule.action_type.replace(/_/g, ' ')}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Triggered</p>
                      <p className="font-medium">{rule.trigger_count || 0} times</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Status</p>
                      <p className="font-medium">
                        {rule.is_active ? (
                          <span className="text-green-600">Active</span>
                        ) : (
                          <span className="text-muted-foreground">Inactive</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleOpenModal(rule)}
                    >
                      <Edit size={14} className="mr-2" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDelete(rule.id)}
                    >
                      <Trash2 size={14} className="mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Automation Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 border border-border rounded-lg">
              <GitBranch size={24} className="mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold">{rules.length}</p>
              <p className="text-xs text-muted-foreground mt-1">Total Rules</p>
            </div>
            <div className="text-center p-4 border border-border rounded-lg">
              <Power size={24} className="mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold">{rules.filter(r => r.is_active).length}</p>
              <p className="text-xs text-muted-foreground mt-1">Active</p>
            </div>
            <div className="text-center p-4 border border-border rounded-lg">
              <Bell size={24} className="mx-auto text-orange-500 mb-2" />
              <p className="text-2xl font-bold">{rules.reduce((sum, r) => sum + (r.trigger_count || 0), 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">Total Triggers</p>
            </div>
            <div className="text-center p-4 border border-border rounded-lg">
              <AlertTriangle size={24} className="mx-auto text-red-500 mb-2" />
              <p className="text-2xl font-bold">{rules.filter(r => r.priority === 'critical').length}</p>
              <p className="text-xs text-muted-foreground mt-1">Critical Rules</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRule ? 'Edit Automation Rule' : 'Create New Automation Rule'}
            </DialogTitle>
            <DialogDescription>
              Set up intelligent rules to monitor your projects and trigger automatic actions
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Rule Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., High Budget Variance Alert"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this rule does..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="trigger_type">Trigger Type *</Label>
                <Select
                  value={formData.trigger_type}
                  onValueChange={(value) => setFormData({ ...formData, trigger_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="variance_exceeded">Budget Variance Exceeded</SelectItem>
                    <SelectItem value="status_change">Project Status Change</SelectItem>
                    <SelectItem value="document_created">Document Created</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="action_type">Action Type *</Label>
                <Select
                  value={formData.action_type}
                  onValueChange={(value) => setFormData({ ...formData, action_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="notify">Send Notification</SelectItem>
                    <SelectItem value="send_email">Send Email</SelectItem>
                    <SelectItem value="flag_project">Flag Project</SelectItem>
                    <SelectItem value="require_approval">Require Approval</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.trigger_type === 'variance_exceeded' && (
              <div className="space-y-2">
                <Label htmlFor="variance_threshold">Variance Threshold (%)</Label>
                <Input
                  id="variance_threshold"
                  type="number"
                  value={formData.variance_threshold}
                  onChange={(e) => setFormData({ ...formData, variance_threshold: e.target.value })}
                  placeholder="10"
                  min="1"
                  max="100"
                />
                <p className="text-xs text-muted-foreground">
                  Trigger when project costs exceed budget by this percentage
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priority">Priority *</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notify_roles">Notify Roles</Label>
                <Select
                  value={formData.notify_roles[0] || ''}
                  onValueChange={(value) => setFormData({ ...formData, notify_roles: [value] })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_active" className="font-normal">
                Activate this rule immediately
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit">
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DecisionTree;
